import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Guitar as Hospital, BookOpen, ArrowRight, CheckCircle, Shield, Clock, ChevronLeft, ChevronRight, Play, Star, Award, Globe, Sparkles, TrendingUp, Zap, Target } from 'lucide-react';
import AnimatedCounter from '../components/AnimatedCounter';

const HomePage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [isProcessVisible, setIsProcessVisible] = useState(false);

  // Generate random numbers for realistic counter animations
  const generateRandomStats = () => ({
    waitingList: Math.floor(Math.random() * 50000) + 100000, // 100k-150k
    livesSaved: Math.floor(Math.random() * 10) + 15, // 15-25
    hospitals: Math.floor(Math.random() * 200) + 400, // 400-600
    uptime: 99.5 + Math.random() * 0.4, // 99.5-99.9
    donorsPerSlide: Math.floor(Math.random() * 5) + 6, // 6-11
    livesEnhanced: Math.floor(Math.random() * 25) + 50, // 50-75
    emergencyHours: 24,
    successRate: Math.floor(Math.random() * 10) + 90, // 90-100
    registeredDonors: Math.floor(Math.random() * 500000) + 1500000, // 1.5M-2M
    countries: Math.floor(Math.random() * 20) + 40, // 40-60
    livesSavedYear: Math.floor(Math.random() * 5000) + 8000, // 8k-13k
    platformSecurity: 100
  });

  // Memoize static data to prevent unnecessary re-renders
  const slides = useMemo(() => {
    const stats = generateRandomStats();
    return [
      {
        id: 1,
        title: "Give the Gift of",
        highlight: "Life",
        subtitle: "Join thousands of heroes who are saving lives through organ donation. One donor can save up to 8 lives and enhance many more.",
        primaryCTA: "Become a Donor",
        primaryLink: "/register-donor",
        secondaryCTA: "Learn More",
        secondaryLink: "/education",
        background: "bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800",
        stats: [
          { number: stats.waitingList, label: 'People on waiting lists', icon: Clock, suffix: '+' },
          { number: stats.livesSaved, label: 'Lives saved by one donor', icon: Heart },
          { number: stats.hospitals, label: 'Partner hospitals', icon: Hospital, suffix: '+' },
          { number: stats.uptime, label: 'Platform uptime', icon: Shield, suffix: '%', decimals: 1 },
        ]
      },
      {
        id: 2,
        title: "Transform Lives",
        highlight: "Today",
        subtitle: "Every 10 minutes, someone is added to the organ transplant waiting list. Your decision to become a donor can be the miracle someone is waiting for.",
        primaryCTA: "Start Registration",
        primaryLink: "/register-donor",
        secondaryCTA: "Success Stories",
        secondaryLink: "/education",
        background: "bg-gradient-to-br from-green-600 via-emerald-700 to-teal-800",
        stats: [
          { number: stats.donorsPerSlide, label: 'Lives saved per donor', icon: Heart },
          { number: stats.livesEnhanced, label: 'Lives enhanced', icon: Users, suffix: '+' },
          { number: stats.emergencyHours, label: 'Emergency support', icon: Clock, suffix: '/7' },
          { number: stats.successRate, label: 'Success rate', icon: Award, suffix: '%' },
        ]
      },
      {
        id: 3,
        title: "Join Our Global",
        highlight: "Mission",
        subtitle: "Be part of a worldwide network of compassionate individuals dedicated to saving lives. Together, we're building a future where no one waits in vain.",
        primaryCTA: "Join Network",
        primaryLink: "/register-donor",
        secondaryCTA: "Watch Video",
        secondaryLink: "/education",
        background: "bg-gradient-to-br from-purple-600 via-violet-700 to-indigo-800",
        stats: [
          { number: stats.registeredDonors, label: 'Registered donors', icon: Users, suffix: '+' },
          { number: stats.countries, label: 'Countries served', icon: Globe, suffix: '+' },
          { number: stats.livesSavedYear, label: 'Lives saved this year', icon: Star, suffix: '+' },
          { number: stats.platformSecurity, label: 'Secure platform', icon: Shield, suffix: '%' },
        ]
      }
    ];
  }, []); // Empty dependency array to generate once on mount

  const features = useMemo(() => [
    {
      icon: Users,
      title: 'Easy Registration',
      description: 'Quick and secure donor registration process with medical verification.',
      color: 'blue'
    },
    {
      icon: Hospital,
      title: 'Hospital Network',
      description: 'Connected with certified medical institutions nationwide.',
      color: 'green'
    },
    {
      icon: Shield,
      title: 'HIPAA Compliant',
      description: 'Your medical data is protected with enterprise-grade security.',
      color: 'purple'
    },
    {
      icon: BookOpen,
      title: 'Education Hub',
      description: 'Comprehensive resources about organ donation and transplantation.',
      color: 'orange'
    }
  ], []);

  const testimonials = useMemo(() => [
    {
      name: "Sarah Johnson",
      role: "Heart Recipient",
      image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150",
      quote: "Thanks to LifeShare, I received a new heart and a second chance at life. The platform made the entire process transparent and gave me hope.",
      rating: 5
    },
    {
      name: "Dr. Michael Chen",
      role: "Transplant Surgeon",
      image: "https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?auto=compress&cs=tinysrgb&w=150",
      quote: "LifeShare has revolutionized how we connect with donors and recipients. The efficiency and security of the platform is unmatched.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Donor Family",
      image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150",
      quote: "During our most difficult time, LifeShare helped us honor our loved one's wish to save lives. Four people received the gift of life.",
      rating: 5
    }
  ], []);

  // Auto-play functionality with cleanup
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  // Reset stats animation when slide changes
  useEffect(() => {
    setIsStatsVisible(false);
    const timer = setTimeout(() => {
      setIsStatsVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  // Intersection Observer for process section animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsProcessVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const processSection = document.getElementById('process-section');
    if (processSection) {
      observer.observe(processSection);
    }

    return () => {
      if (processSection) {
        observer.unobserve(processSection);
      }
    };
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const currentSlideData = slides[currentSlide];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return 'bg-blue-100 text-blue-600 group-hover:bg-blue-200 group-hover:text-blue-700';
      case 'green':
        return 'bg-green-100 text-green-600 group-hover:bg-green-200 group-hover:text-green-700';
      case 'purple':
        return 'bg-purple-100 text-purple-600 group-hover:bg-purple-200 group-hover:text-purple-700';
      case 'orange':
        return 'bg-orange-100 text-orange-600 group-hover:bg-orange-200 group-hover:text-orange-700';
      default:
        return 'bg-blue-100 text-blue-600 group-hover:bg-blue-200 group-hover:text-blue-700';
    }
  };

  return (
    <div className="space-y-0 overflow-hidden">
      {/* Hero Carousel Section */}
      <section className={`relative ${currentSlideData.background} text-white overflow-hidden transition-all duration-1000 ease-in-out min-h-screen`}>
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse top-1/4 left-1/4" />
          <div className="absolute w-80 h-80 bg-white/5 rounded-full blur-3xl animate-pulse bottom-1/4 right-1/4" style={{ animationDelay: '1s' }} />
        </div>

        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[600px]">
            {/* Content */}
            <div className="space-y-8 transform transition-all duration-700 ease-out animate-fade-in-up">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-3 bg-white/15 backdrop-blur-md rounded-full px-6 py-3 text-sm font-medium border border-white/30 shadow-lg">
                  <Heart className="h-5 w-5 animate-pulse text-red-300" />
                  <span className="text-white/90">Slide {currentSlide + 1} of {slides.length}</span>
                  <Sparkles className="h-5 w-5 animate-spin text-yellow-300" style={{ animationDuration: '3s' }} />
                </div>
                
                <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                  <span className="animate-fade-in-up block mb-4 bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent" style={{ animationDelay: '0.2s' }}>
                    {currentSlideData.title}
                  </span>
                  <span className="text-yellow-300 block animate-bounce mt-6 drop-shadow-lg" style={{ animationDelay: '0.4s' }}>
                    {currentSlideData.highlight}
                  </span>
                </h1>
                
                <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed max-w-2xl animate-fade-in-up drop-shadow-sm" style={{ animationDelay: '0.6s' }}>
                  {currentSlideData.subtitle}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
                <Link
                  to={currentSlideData.primaryLink}
                  className="group bg-white/95 backdrop-blur-sm text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-xl border border-white/30"
                >
                  <Heart className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                  {currentSlideData.primaryCTA}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to={currentSlideData.secondaryLink}
                  className="group border-2 border-white/60 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/15 transition-all duration-300 flex items-center justify-center"
                >
                  {currentSlideData.secondaryCTA}
                  {currentSlideData.secondaryCTA.includes('Video') && (
                    <Play className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                  )}
                </Link>
              </div>

              {/* Progress Bar */}
              <div className="flex space-x-3 animate-fade-in-up" style={{ animationDelay: '1s' }}>
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`h-2 rounded-full transition-all duration-500 hover:scale-110 ${
                      index === currentSlide ? 'bg-white w-16 shadow-lg' : 'bg-white/40 w-8 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            {/* Enhanced Stats Card with Random Numbers */}
            <div className="relative animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="bg-white/15 backdrop-blur-xl rounded-3xl p-8 space-y-6 border border-white/30 shadow-2xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-center flex items-center space-x-2">
                    <TrendingUp className="h-6 w-6 text-yellow-300" />
                    <span>Live Impact</span>
                  </h3>
                  <div className="flex items-center space-x-2 text-sm text-white/80 bg-green-500/20 px-3 py-1 rounded-full">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span>Real-time</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  {currentSlideData.stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <div key={index} className="text-center space-y-3 group hover:scale-110 transition-all duration-300 p-4 rounded-2xl hover:bg-white/15 cursor-pointer">
                        <div className="bg-white/25 backdrop-blur-sm rounded-2xl p-4 w-20 h-20 mx-auto flex items-center justify-center group-hover:bg-white/35 transition-all duration-300 shadow-lg">
                          <Icon className="h-10 w-10 text-yellow-300 group-hover:animate-pulse" />
                        </div>
                        <div className="text-4xl font-bold text-white drop-shadow-lg">
                          <AnimatedCounter
                            end={stat.number}
                            duration={2000 + Math.random() * 1000} // Random duration for variety
                            suffix={stat.suffix || ''}
                            decimals={stat.decimals || 0}
                            startAnimation={isStatsVisible}
                            delay={index * 200 + Math.random() * 300} // Random delay for staggered effect
                            className="animate-counter-glow"
                          />
                        </div>
                        <div className="text-sm text-blue-100 leading-tight font-medium">{stat.label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Auto-play Toggle */}
                <div className="flex items-center justify-center space-x-3 pt-4 border-t border-white/30">
                  <button
                    onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 backdrop-blur-sm border ${
                      isAutoPlaying 
                        ? 'bg-green-500/30 text-green-200 border-green-400/50' 
                        : 'bg-gray-500/30 text-gray-200 border-gray-400/50'
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-full ${isAutoPlaying ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
                    <span>{isAutoPlaying ? 'Auto-playing' : 'Paused'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="absolute inset-y-0 left-4 flex items-center">
          <button
            onClick={prevSlide}
            className="bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-full p-4 transition-all duration-300 group border border-white/30 shadow-xl"
          >
            <ChevronLeft className="h-6 w-6 text-white group-hover:scale-110 transition-transform" />
          </button>
        </div>

        <div className="absolute inset-y-0 right-4 flex items-center">
          <button
            onClick={nextSlide}
            className="bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-full p-4 transition-all duration-300 group border border-white/30 shadow-xl"
          >
            <ChevronRight className="h-6 w-6 text-white group-hover:scale-110 transition-transform" />
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="flex space-x-4 bg-black/30 backdrop-blur-md rounded-full px-6 py-3 border border-white/30">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-4 h-4 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-white scale-125 shadow-lg' 
                    : 'bg-white/50 hover:bg-white/75 hover:scale-110'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center space-y-6 mb-20 animate-fade-in-up">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium">
              <Zap className="h-4 w-4" />
              <span>Why Choose LifeShare</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              Cutting-Edge Platform for
              <span className="block text-blue-600 mt-2">Life-Saving Connections</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our platform connects donors, recipients, and medical professionals 
              with cutting-edge technology and compassionate care.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="group text-center space-y-6 p-8 rounded-2xl hover:bg-white hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-transparent hover:border-gray-100 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`p-6 rounded-3xl w-20 h-20 mx-auto flex items-center justify-center transition-all duration-300 shadow-lg group-hover:shadow-xl ${getColorClasses(feature.color)}`}>
                    <Icon className="h-10 w-10 group-hover:scale-110 transition-transform" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enhanced Process Section with Animations - NO UNDERLINES */}
      <section id="process-section" className="py-24 bg-gradient-to-br from-blue-50 to-indigo-100 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute top-3/4 left-3/4 w-48 h-48 bg-purple-200/25 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center space-y-6 mb-20 animate-fade-in-up">
            <div className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
              <Target className="h-4 w-4" />
              <span>Simple Process</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              Three Steps to
              <span className="block text-blue-600 mt-2">Life-Changing Impact</span>
            </h2>
          </div>
          
          {/* Fixed Grid Layout with Proper Alignment - NO UNDERLINES */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-8">
            {[
              {
                step: 1,
                title: "Register Online",
                description: "Complete our secure registration form with your personal and medical information in just 5 minutes.",
                icon: Users,
                color: "blue",
                bgGradient: "from-blue-500 to-blue-600",
                glowColor: "blue-400"
              },
              {
                step: 2,
                title: "Medical Verification",
                description: "Our medical team reviews your information and coordinates with healthcare providers for verification.",
                icon: Shield,
                color: "green",
                bgGradient: "from-green-500 to-green-600",
                glowColor: "green-400"
              },
              {
                step: 3,
                title: "Save Lives",
                description: "Your generous donation can save up to 8 lives and help countless others through tissue donation.",
                icon: Heart,
                color: "red",
                bgGradient: "from-red-500 to-red-600",
                glowColor: "red-400"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div 
                  key={index}
                  className={`relative text-center group transform transition-all duration-700 ${
                    isProcessVisible 
                      ? 'translate-y-0 opacity-100' 
                      : 'translate-y-20 opacity-0'
                  }`}
                  style={{ 
                    transitionDelay: `${index * 200}ms`,
                    animationDelay: `${index * 200}ms`
                  }}
                >
                  {/* Animated Connection Line - Only between steps */}
                  {index < 2 && (
                    <div className="hidden lg:block absolute top-12 left-full w-full h-1 z-0 pointer-events-none">
                      <div className={`h-full bg-gradient-to-r from-${item.color}-300 to-transparent rounded-full transform transition-all duration-1000 ${
                        isProcessVisible ? 'scale-x-100' : 'scale-x-0'
                      }`} style={{ transformOrigin: 'left', transitionDelay: `${(index + 1) * 300}ms` }} />
                      
                      {/* Animated Dots */}
                      <div className={`absolute top-1/2 left-1/4 w-2 h-2 bg-${item.color}-400 rounded-full transform -translate-y-1/2 transition-all duration-500 ${
                        isProcessVisible ? 'animate-pulse' : 'opacity-0'
                      }`} style={{ transitionDelay: `${(index + 1) * 400}ms` }} />
                      <div className={`absolute top-1/2 left-2/4 w-2 h-2 bg-${item.color}-400 rounded-full transform -translate-y-1/2 transition-all duration-500 ${
                        isProcessVisible ? 'animate-pulse' : 'opacity-0'
                      }`} style={{ transitionDelay: `${(index + 1) * 500}ms` }} />
                      <div className={`absolute top-1/2 left-3/4 w-2 h-2 bg-${item.color}-400 rounded-full transform -translate-y-1/2 transition-all duration-500 ${
                        isProcessVisible ? 'animate-pulse' : 'opacity-0'
                      }`} style={{ transitionDelay: `${(index + 1) * 600}ms` }} />
                    </div>
                  )}

                  {/* Step Number and Icon Container - Centered */}
                  <div className="relative flex justify-center mb-8">
                    {/* Animated Glow Effect */}
                    <div className={`absolute inset-0 bg-${item.glowColor} rounded-full blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 transform scale-150`} />
                    
                    {/* Main Step Circle - Fixed Size and Centered */}
                    <div className={`relative bg-gradient-to-br ${item.bgGradient} text-white w-24 h-24 rounded-2xl flex items-center justify-center text-3xl font-bold group-hover:scale-110 transition-all duration-500 shadow-2xl z-10 transform ${
                      isProcessVisible ? 'rotate-0' : 'rotate-180'
                    }`} style={{ transitionDelay: `${index * 150}ms` }}>
                      <span className="relative z-10">{item.step}</span>
                      
                      {/* Animated Ring */}
                      <div className={`absolute inset-0 border-4 border-white/30 rounded-2xl animate-pulse`} />
                    </div>
                    
                    {/* Floating Icon - Positioned Consistently */}
                    <div className={`absolute -top-3 -right-3 bg-white rounded-full p-3 shadow-lg group-hover:scale-110 transition-all duration-500 z-20 transform ${
                      isProcessVisible ? 'translate-y-0 rotate-0' : 'translate-y-4 rotate-45'
                    }`} style={{ transitionDelay: `${index * 200 + 100}ms` }}>
                      <Icon className={`h-7 w-7 text-${item.color}-600 group-hover:animate-bounce`} />
                    </div>

                    {/* Orbiting Particles */}
                    <div className={`absolute inset-0 ${isProcessVisible ? 'animate-spin' : ''}`} style={{ animationDuration: '8s', animationDelay: `${index * 1000}ms` }}>
                      <div className={`absolute -top-1 left-1/2 w-2 h-2 bg-${item.color}-400 rounded-full transform -translate-x-1/2`} />
                    </div>
                    <div className={`absolute inset-0 ${isProcessVisible ? 'animate-spin' : ''}`} style={{ animationDuration: '6s', animationDirection: 'reverse', animationDelay: `${index * 800}ms` }}>
                      <div className={`absolute top-1/2 -right-1 w-1.5 h-1.5 bg-${item.color}-300 rounded-full transform -translate-y-1/2`} />
                    </div>
                  </div>
                  
                  {/* Content - Clean Design Without Underlines */}
                  <div className={`space-y-6 transform transition-all duration-700 ${
                    isProcessVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`} style={{ transitionDelay: `${index * 250 + 200}ms` }}>
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 mb-6">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed max-w-sm mx-auto group-hover:text-gray-700 transition-colors duration-300">
                      {item.description}
                    </p>
                  </div>

                  {/* Hover Effect Background */}
                  <div className="absolute inset-0 bg-white/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 transform scale-95 group-hover:scale-100" />
                </div>
              );
            })}
          </div>

          {/* Call to Action */}
          <div className={`text-center mt-16 transform transition-all duration-700 ${
            isProcessVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '1000ms' }}>
            <Link
              to="/register-donor"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 shadow-xl group"
            >
              <Heart className="h-6 w-6 group-hover:animate-pulse" />
              <span>Start Your Journey Today</span>
              <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6 mb-20 animate-fade-in-up">
            <div className="inline-flex items-center space-x-2 bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-medium">
              <Heart className="h-4 w-4" />
              <span>Success Stories</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              Real Stories from
              <span className="block text-green-600 mt-2">Our Community</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-green-200 group animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center space-x-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-green-200 group-hover:border-green-400 transition-colors"
                    loading="lazy"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-green-600 via-emerald-700 to-teal-800 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-10 relative">
          <div className="space-y-6 animate-fade-in-up">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium border border-white/30">
              <Sparkles className="h-4 w-4 animate-spin" style={{ animationDuration: '3s' }} />
              <span>Join the Movement</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold leading-tight">
              Ready to Make a
              <span className="block text-yellow-300 mt-2">Difference?</span>
            </h2>
            <p className="text-xl lg:text-2xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              Join our community of life-savers. Your decision today can transform tomorrow for someone in need.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <Link
              to="/register-donor"
              className="group bg-white/95 backdrop-blur-sm text-green-600 px-10 py-5 rounded-xl font-bold text-xl hover:bg-white transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-2xl border border-white/30"
            >
              <Heart className="mr-3 h-6 w-6 group-hover:animate-pulse" />
              Register as Donor
              <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/login"
              className="group border-2 border-white/60 backdrop-blur-sm text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-white/15 transition-all duration-300 flex items-center justify-center"
            >
              Healthcare Provider Login
            </Link>
          </div>
          
          <div className="flex items-center justify-center space-x-6 text-green-100 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 animate-pulse" />
              <span className="font-medium">HIPAA Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span className="font-medium">Secure Platform</span>
            </div>
            <div className="flex items-center space-x-2">
              <Hospital className="h-5 w-5" />
              <span className="font-medium">500+ Hospitals</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;